import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  BuildingStorefrontIcon,
  PaperAirplaneIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const TRUELOG_BLUE = '#385CF2';
const TRUELOG_CYAN = '#0E9ED5';

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const serviceCards = [
  {
    icon: ShieldCheckIcon,
    title: 'Trade Compliance',
    description: 'IOR/EOR & customs clearance in 140+ countries',
    href: '/services/cross-border-compliance',
    accent: 'from-[#385CF2] to-[#0E9ED5]',
  },
  {
    icon: PaperAirplaneIcon,
    title: 'Global Transport & Freight',
    description: 'Secure, reliable air & sea transport',
    href: '/services/global-transport-freight',
    accent: 'from-[#0E9ED5] to-[#385CF2]',
  },
  {
    icon: BuildingStorefrontIcon,
    title: 'Warehousing & Fulfilment',
    description: 'Inventory ready for fast deployment',
    href: '/services/warehousing-fulfilment',
    accent: 'from-[#385CF2] to-[#0E9ED5]',
  },
  {
    icon: ServerStackIcon,
    title: 'Value Added Services',
    description: 'Reverse logistics, RMA, kitting, pick & pack',
    href: '/contact-us',
    cta: 'Talk to us',
    accent: 'from-[#0E9ED5] to-[#385CF2]',
  },
];

const Services: React.FC = () => {
  return (
    <section id="services" className="relative overflow-hidden py-14 lg:py-20">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-0 top-20 h-72 w-72 rounded-full bg-[#385CF2]/8 blur-[120px]" />
        <div className="absolute right-0 top-1/3 h-80 w-80 rounded-full bg-[#0E9ED5]/10 blur-[140px]" />
      </div>

      <div className="container-custom relative">
        <motion.div
          {...reveal}
          transition={{ duration: 0.75 }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="eyebrow-pill">
            <SparklesIcon className="h-3.5 w-3.5" />
            What We Do
          </span>

          <h2 className="mt-5 text-slate-950">One partner. <span className="whitespace-nowrap">End-to-end</span> ICT logistics.</h2>

          <p className="mt-5 text-base leading-8 text-slate-600 sm:text-lg">
            From cross-border compliance to freight, warehousing and reverse logistics, we provide end-to-end solutions designed
            to optimise your supply chain and drive business growth.
          </p>
        </motion.div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {serviceCards.map((service, index) => (
            <motion.article
              key={service.title}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: index * 0.08 }}
              className="group flex h-full flex-col rounded-[30px] border border-slate-200/70 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.24)] transition-all duration-300 hover:-translate-y-1 hover:border-cyan-200 hover:shadow-[0_32px_70px_-40px_rgba(15,23,42,0.34)]"
            >
              <div className={`h-1.5 w-full rounded-full bg-gradient-to-r ${service.accent}`} />

              <div className={`mt-6 inline-flex w-fit rounded-2xl bg-gradient-to-br ${service.accent} p-3 text-white`}>
                <service.icon className="h-6 w-6" />
              </div>

              <h3 className="mt-5 text-xl font-semibold text-slate-950">{service.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{service.description}</p>

              <Link
                to={service.href}
                className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-semibold text-[#2447d8] transition-colors hover:text-[#0E9ED5]"
              >
                {service.cta ?? 'Learn more'}
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
            </motion.article>
          ))}
        </div>

        <motion.div
          {...reveal}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-10 flex justify-center"
        >
          <Link
            to="/services"
            className="inline-flex items-center justify-center rounded-2xl px-7 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-[0_20px_44px_-20px_rgba(14,158,213,0.55)] transition-all duration-300 hover:-translate-y-0.5"
            style={{ background: `linear-gradient(135deg, ${TRUELOG_CYAN} 0%, ${TRUELOG_BLUE} 100%)` }}
          >
            Explore our Solutions
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
